import { Button, HideIcon, TextInput, ViewIcon } from '@medeloop/medeloop-components-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { changePassword } from 'src/api/users';
import { LoadingLoop } from 'src/components';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { showToast } from 'src/store/toast';
import { classnames } from 'src/utils/classnames';

export const ChangePasswordPage = () => {
  const token = useSelector(selectAuthToken);
  const accessToken = useSelector(selectAccount).accessToken;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [oldPassword, setOldPassword] = useState('');
  const [hideOldPassword, setHideOldPassword] = useState(true);

  const [newPassword, setNewPassword] = useState('');
  const [hideNewPassword, setHideNewPassword] = useState(true);

  const [confirmPassword, setConfirmPassword] = useState('');
  const [hideConfirmPassword, setHideConfirmPassword] = useState(true);

  const changePasswordMutation = useMutation({
    mutationFn: () => {
      if (newPassword !== confirmPassword) {
        throw Error(t('new_passwords_dont_match'));
      }
      return changePassword(token, { accessToken, oldPassword, newPassword });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          message: t('success_password_reseted'),
        }),
      );
    },
  });

  const handleChangePassword = () => {
    changePasswordMutation.mutate();
  };

  return (
    <div className="flex flex-col bg-gray-900 rounded-lg p-6 mt-4 max-w-3xl">
      <div className="flex w-full">
        <div className="flex flex-col text-white pb-2">
          <span className="text-lg font-semibold">{t('change_password')}</span>
        </div>
      </div>

      {changePasswordMutation.error instanceof Error && (
        <div
          className={classnames(
            'flex rounded-md flex-grow items-center justify-center',
            ' bg-red-100 p-4 text-red-500 font-semibold mt-4',
          )}
        >
          {changePasswordMutation.error.message}
        </div>
      )}

      {changePasswordMutation.isPending && (
        <div className="flex flex-grow items-center justify-center my-8">
          <LoadingLoop className="w-16" />
        </div>
      )}

      {!changePasswordMutation.isPending && (
        <div className="flex flex-col text-sm text-white ">
          <div className="flex flex-col my-4 bg-gray-950 rounded-md p-6">
            <div className="flex gap-12">
              <div className="flex flex-col text-white w-72">
                <TextInput
                  type={hideOldPassword ? 'password' : 'text'}
                  label={t('old_password')}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  postChildren={
                    <div onClick={() => setHideOldPassword(!hideOldPassword)}>
                      {hideOldPassword ? (
                        <ViewIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <HideIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  }
                />
              </div>
            </div>
            <div className="flex gap-12 py-8">
              <div className="flex flex-col w-72">
                <TextInput
                  type={hideNewPassword ? 'password' : 'text'}
                  label={t('new_password')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  postChildren={
                    <div onClick={() => setHideNewPassword(!hideNewPassword)}>
                      {hideNewPassword ? (
                        <ViewIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <HideIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  }
                />
              </div>
              <div className="flex flex-col w-72">
                <TextInput
                  type={hideConfirmPassword ? 'password' : 'text'}
                  label={t('confirm_password')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  postChildren={
                    <div onClick={() => setHideConfirmPassword(!hideConfirmPassword)}>
                      {hideConfirmPassword ? (
                        <ViewIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <HideIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  }
                />
              </div>
            </div>
            <div className="w-full flex items-end justify-end">
              <Button onClick={handleChangePassword} variant="primary">
                {t('change_password')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
